import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, RefreshCw, Search, Users, ChevronLeft, ChevronRight,
  UserCheck, UserX, Lock, Unlock, KeyRound, MoreVertical,
  CheckCircle2, XCircle, Clock, AlertTriangle,
} from 'lucide-react';

import { userApi }       from '@/api/user.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { AppRoutes }     from '@/constants/routes';
import { Spinner }       from '@/components/ui/Spinner';
import { cn }            from '@/utils/cn';

import type { UserListDto, UserFilterDto, UserStatus } from '@/types/user.types';
import { USER_STATUS_LABELS, USER_STATUS_COLORS, USER_TYPE_LABELS, avatarColor } from '@/types/user.types';

// ── Status icon ────────────────────────────────────────────────────────────────

const StatusIcon = ({ status }: { status: UserStatus }) => {
  const base = 'shrink-0';
  switch (status) {
    case 'Active':            return <CheckCircle2 size={14} className={cn(base, 'text-green-500')} />;
    case 'Inactive':          return <XCircle      size={14} className={cn(base, 'text-gray-400')} />;
    case 'Locked':            return <Lock         size={14} className={cn(base, 'text-red-500')} />;
    case 'PendingActivation': return <Clock        size={14} className={cn(base, 'text-amber-500')} />;
  }
};

// ── Reset Password Modal ───────────────────────────────────────────────────────

interface ResetPwModalProps {
  userId: string;
  userName: string;
  onClose: () => void;
  onDone:  () => void;
}

const ResetPwModal = ({ userId, userName, onClose, onDone }: ResetPwModalProps) => {
  const [password,       setPassword]       = useState('');
  const [force,          setForce]          = useState(true);
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!password.trim()) { setError('Password is required.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setSaving(true);
    try {
      await userApi.resetPassword(userId, { newPassword: password, forceChangeOnLogin: force });
      onDone();
    } catch {
      setError('Failed to reset password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Reset Password</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          Set a new temporary password for <span className="font-medium text-slate-700 dark:text-slate-200">{userName}</span>.
        </p>

        {error && (
          <div className="mb-4 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
              New Password
            </label>
            <input
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); setError(null); }}
              placeholder="Minimum 8 characters"
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600
                         rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={force}
              onChange={e => setForce(e.target.checked)}
              className="rounded border-slate-300 text-blue-600"
            />
            <span className="text-sm text-slate-700 dark:text-slate-300">
              Force password change on next login
            </span>
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600
                       text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white
                       hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {saving ? <Spinner size="sm" /> : <KeyRound size={14} />}
            Reset Password
          </button>
        </div>
      </div>
    </div>
  );
};

// ── User List Page ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

export const UserListPage = () => {
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();

  const canCreate    = hasPermission(Permissions.ADMIN_USER_CREATE);
  const canEdit      = hasPermission(Permissions.ADMIN_USER_EDIT);
  const canDisable   = hasPermission(Permissions.ADMIN_USER_EDIT);
  const canLock      = hasPermission(Permissions.ADMIN_USER_EDIT);
  const canResetPw   = hasPermission(Permissions.ADMIN_USER_RESET_PW);

  const [items,    setItems]    = useState<UserListDto[]>([]);
  const [loading,  setLoading]  = useState(false);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [search,   setSearch]   = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [actionMenuId,  setActionMenuId]  = useState<string | null>(null);
  const [resetPwUser,   setResetPwUser]   = useState<{ id: string; name: string } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async (p: number, s: string, st: string) => {
    setLoading(true);
    try {
      const filter: UserFilterDto = {
        pageNumber:  p,
        pageSize:    PAGE_SIZE,
        searchTerm:  s || undefined,
        status:      st || undefined,
        sortBy:      'fullName',
        sortDescending: false,
      };
      const res = await userApi.getList(filter);
      const data = res.data?.data;
      if (data) {
        setItems(data.items);
        setTotal(data.totalCount);
      }
    } catch {
      showToast('Failed to load users.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page, search, statusFilter); }, [page, load, search, statusFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    load(1, search, statusFilter);
  };

  const handleStatusFilter = (s: string) => {
    setStatusFilter(s);
    setPage(1);
  };

  // ── Quick actions ────────────────────────────────────────────────────────────

  const performAction = async (
    action: () => Promise<unknown>,
    successMsg: string,
  ) => {
    setActionLoading(true);
    setActionMenuId(null);
    try {
      await action();
      showToast(successMsg);
      load(page, search, statusFilter);
    } catch {
      showToast('Action failed. Please try again.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisable = (id: string) =>
    performAction(() => userApi.disable(id), 'User disabled.');
  const handleEnable  = (id: string) =>
    performAction(() => userApi.enable(id),  'User enabled.');
  const handleLock    = (id: string) =>
    performAction(() => userApi.lock(id),    'User locked.');
  const handleUnlock  = (id: string) =>
    performAction(() => userApi.unlock(id),  'User unlocked.');

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* Toast */}
      {toast && (
        <div className={cn(
          'fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 transition-all',
          toast.type === 'success'
            ? 'bg-green-600 text-white'
            : 'bg-red-600 text-white',
        )}>
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          {toast.message}
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPwUser && (
        <ResetPwModal
          userId={resetPwUser.id}
          userName={resetPwUser.name}
          onClose={() => setResetPwUser(null)}
          onDone={() => {
            setResetPwUser(null);
            showToast('Password reset successfully.');
            load(page, search, statusFilter);
          }}
        />
      )}

      {/* ── Header ───────────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Users size={20} className="text-blue-600" />
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white">User Management</h1>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {total > 0
                ? `${total.toLocaleString()} user${total !== 1 ? 's' : ''}`
                : 'No users found'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load(page, search, statusFilter)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            {canCreate && (
              <button
                onClick={() => navigate(AppRoutes.ADMIN_USER_NEW)}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium
                           rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus size={16} /> New User
              </button>
            )}
          </div>
        </div>

        {/* ── Search + Filters ───────────────────────────────────────────────── */}
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search name, username, email…"
                className="pl-9 pr-3 py-2 text-sm border border-slate-300 dark:border-slate-600
                           rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white w-64
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              type="submit"
              className="px-3 py-2 text-sm font-medium bg-slate-100 dark:bg-slate-700
                         text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
            >
              Search
            </button>
          </form>

          {/* Status tabs */}
          <div className="flex items-center gap-1">
            {(['', 'Active', 'Inactive', 'Locked'] as const).map(s => (
              <button
                key={s}
                onClick={() => handleStatusFilter(s)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-full transition-colors',
                  statusFilter === s
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600',
                )}
              >
                {s || 'All'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Spinner size="lg" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <Users size={40} className="mb-3 opacity-30" />
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">No users found</p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
              {search || statusFilter ? 'Try adjusting your search or filters.' : 'Create your first user to get started.'}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Username / Code</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Last Login</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(user => (
                <tr
                  key={user.id}
                  className="hover:bg-blue-50 dark:hover:bg-slate-700/50 transition-colors"
                >
                  {/* Name + avatar */}
                  <td
                    className="px-4 py-3 cursor-pointer"
                    onClick={() => navigate(`${AppRoutes.ADMIN_USERS}/${user.id}`)}
                  >
                    <div className="flex items-center gap-2.5">
                      {/* Avatar: profile image or coloured initials */}
                      {user.profileImageUrl ? (
                        <img
                          src={user.profileImageUrl}
                          alt={user.fullName}
                          className="w-8 h-8 rounded-full object-cover shrink-0 border border-slate-200 dark:border-slate-600"
                        />
                      ) : (
                        <div className={cn(
                          'w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0',
                          avatarColor(user.fullName),
                        )}>
                          {user.fullName.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                          {user.displayName || user.fullName}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {user.displayName ? user.fullName : ''}
                          {user.isSystemUser && (
                            <span className="text-purple-600 dark:text-purple-400">System User</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Username / Code */}
                  <td className="px-4 py-3">
                    <p className="font-mono text-xs text-slate-700 dark:text-slate-300">@{user.username}</p>
                    {user.userCode && (
                      <p className="font-mono text-xs text-slate-400 dark:text-slate-500">#{user.userCode}</p>
                    )}
                  </td>

                  {/* Email */}
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400 text-xs">
                    {user.email}
                  </td>

                  {/* Type */}
                  <td className="px-4 py-3">
                    {user.userType ? (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                        {USER_TYPE_LABELS[user.userType] ?? user.userType}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400 dark:text-gray-500">—</span>
                    )}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1.5">
                      <StatusIcon status={user.status} />
                      <span className={cn(
                        'px-2 py-0.5 rounded-full text-xs font-medium',
                        USER_STATUS_COLORS[user.status],
                      )}>
                        {USER_STATUS_LABELS[user.status]}
                      </span>
                    </div>
                    {user.failedLoginAttempts > 0 && user.status !== 'Locked' && (
                      <p className="text-center text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                        {user.failedLoginAttempts} failed attempt{user.failedLoginAttempts !== 1 ? 's' : ''}
                      </p>
                    )}
                  </td>

                  {/* Last Login */}
                  <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                    {user.lastLoginDate
                      ? new Date(user.lastLoginDate).toLocaleDateString('en-GB', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })
                      : <span className="italic">Never</span>}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1.5 relative">
                      {canEdit && (
                        <button
                          onClick={() => navigate(`${AppRoutes.ADMIN_USERS}/${user.id}`)}
                          className="px-2.5 py-1 text-xs font-medium rounded-lg
                                     bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300
                                     hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                        >
                          Edit
                        </button>
                      )}

                      {/* More actions dropdown */}
                      {(canDisable || canLock || canResetPw) && !user.isSystemUser && (
                        <div className="relative">
                          <button
                            onClick={() => setActionMenuId(actionMenuId === user.id ? null : user.id)}
                            className="p-1.5 rounded-lg text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                          >
                            <MoreVertical size={14} />
                          </button>

                          {actionMenuId === user.id && (
                            <div className="absolute right-0 top-8 z-20 w-44 bg-white dark:bg-slate-800 rounded-lg shadow-lg
                                            border border-slate-200 dark:border-slate-700 py-1">
                              {canDisable && user.status === 'Active' && (
                                <button
                                  onClick={() => handleDisable(user.id)}
                                  disabled={actionLoading}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-300
                                             hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                >
                                  <UserX size={14} className="text-amber-500" /> Disable User
                                </button>
                              )}
                              {canDisable && user.status === 'Inactive' && (
                                <button
                                  onClick={() => handleEnable(user.id)}
                                  disabled={actionLoading}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-300
                                             hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                >
                                  <UserCheck size={14} className="text-green-500" /> Enable User
                                </button>
                              )}
                              {canLock && user.status !== 'Locked' && (
                                <button
                                  onClick={() => handleLock(user.id)}
                                  disabled={actionLoading}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-300
                                             hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                >
                                  <Lock size={14} className="text-red-500" /> Lock Account
                                </button>
                              )}
                              {canLock && user.status === 'Locked' && (
                                <button
                                  onClick={() => handleUnlock(user.id)}
                                  disabled={actionLoading}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-300
                                             hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                >
                                  <Unlock size={14} className="text-green-500" /> Unlock Account
                                </button>
                              )}
                              {canResetPw && (
                                <>
                                  <div className="border-t border-slate-100 dark:border-slate-700 my-1" />
                                  <button
                                    onClick={() => {
                                      setActionMenuId(null);
                                      setResetPwUser({ id: user.id, name: user.fullName });
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-300
                                               hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                  >
                                    <KeyRound size={14} className="text-blue-500" /> Reset Password
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ─────────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700
                        bg-white dark:bg-slate-800 px-6 py-3">
          <p className="text-sm text-slate-500 dark:text-gray-400">
            Page {page} of {totalPages} · {total.toLocaleString()} users
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => p - 1)}
              disabled={page <= 1}
              className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const n = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
              return (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  className={cn(
                    'w-8 h-8 text-sm rounded transition-colors',
                    n === page
                      ? 'bg-blue-600 text-white font-semibold'
                      : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300',
                  )}
                >
                  {n}
                </button>
              );
            })}
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages}
              className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Click-outside to close action menu */}
      {actionMenuId && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setActionMenuId(null)}
        />
      )}
    </div>
  );
};
